import { useEffect, useRef, useState } from "react";

// Scroll-spy for the desktop rail: reports which industry block currently
// occupies the most of the viewport, so the rail can highlight it as you scroll.
// Mirrors the "most visible pixels wins" approach used by the section nav tracker
// (stable across boundaries; no flip-flop).
export function useIndustryScrollSpy(count: number) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const blocks = Array.from(
      root.querySelectorAll<HTMLElement>("[data-industry-index]"),
    );
    const visible = new Map<number, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const idx = Number((entry.target as HTMLElement).dataset.industryIndex);
          visible.set(idx, entry.isIntersecting ? entry.intersectionRect.height : 0);
        }
        let bestIdx = -1;
        let bestPx = 0;
        for (let i = 0; i < count; i++) {
          const px = visible.get(i) ?? 0;
          if (px > bestPx) {
            bestPx = px;
            bestIdx = i;
          }
        }
        // Nothing in view (scrolled above/below the section) — keep the last
        // marker instead of snapping back to the first industry.
        if (bestIdx === -1) return;
        setActiveIndex((prev) => (prev === bestIdx ? prev : bestIdx));
      },
      { threshold: Array.from({ length: 11 }, (_, i) => i / 10) },
    );

    blocks.forEach((block) => observer.observe(block));
    return () => observer.disconnect();
  }, [count]);

  return { containerRef, activeIndex };
}
